import { type ChangeEvent, type ReactElement, useEffect, useRef, useState } from "react"
import styled, { keyframes } from "styled-components"
import { Provenance } from "~/features/provenance"
import { type Ministry, ministryLabel } from "~/shared/theme/audience"
import { Button, Gift } from "~/shared/ui"
import { normalizeCode, type RedeemOutcome, useRedeemCode } from "../api/useRedeemCode"
import { redeemFailureMessage } from "../redeemMessages"
import type { EntitlementItem } from "../types"

const QA = "redeem-code"
const AUTO_LENGTH = 12
const DEBOUNCE_MS = 350
/**
 * Spelled out rather than derived from a count inside the render: a piece's
 * offset, tint and delay are the whole of its identity, so naming them here
 * gives each one a stable key and keeps the arithmetic out of the styled
 * component.
 */
const CONFETTI_PIECES = [
  { id: "ember-0", offsetPercent: 0, tint: "harvestEmber", delayMs: 0 },
  { id: "kingdom-1", offsetPercent: 10, tint: "kingdomLight", delayMs: 70 },
  { id: "rooted-2", offsetPercent: 20, tint: "rootedRadiance", delayMs: 140 },
  { id: "mercies-3", offsetPercent: 30, tint: "newMercies", delayMs: 210 },
  { id: "accent-4", offsetPercent: 40, tint: "accent", delayMs: 280 },
  { id: "ember-5", offsetPercent: 50, tint: "harvestEmber", delayMs: 0 },
  { id: "kingdom-6", offsetPercent: 60, tint: "kingdomLight", delayMs: 70 },
  { id: "rooted-7", offsetPercent: 70, tint: "rootedRadiance", delayMs: 140 },
  { id: "mercies-8", offsetPercent: 80, tint: "newMercies", delayMs: 210 },
  { id: "accent-9", offsetPercent: 90, tint: "accent", delayMs: 280 },
] as const

type ConfettiTint = (typeof CONFETTI_PIECES)[number]["tint"]

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`

/** The collapsed trigger sizes to its label: the page column would stretch it across the full width. */
const Trigger = styled.div`
  align-self: flex-start;
`

/** Likewise inside the confirmation panel, which is a column of its own. */
const RedeemAnother = styled(Button)`
  align-self: flex-start;
`

const Capsule = styled.div`
  display: inline-flex;
  align-items: stretch;
`

const CapsuleTrigger = styled(Button)`
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  cursor: default;

  &:hover {
    background-color: ${({ theme }) => theme.color.accent};
  }
`

const Input = styled.input`
  min-width: 14rem;
  flex: 1 1 14rem;
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.md}`};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  background-color: ${({ theme }) => theme.color.controlSurface};
  border: 1px solid ${({ theme }) => theme.color.borderStrong};
  border-left: none;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-top-right-radius: ${({ theme }) => theme.radius.pill};
  border-bottom-right-radius: ${({ theme }) => theme.radius.pill};

  &:disabled {
    opacity: 0.6;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 1px;
  }
`

const Pending = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing.xs} 0`};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textSecondary};
`

const Panel = styled.div<{ $tone: "success" | "error" }>`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.radius.lg};
  color: ${({ theme, $tone }) => ($tone === "success" ? theme.color.success : theme.color.error)};
  background-color: ${({ theme, $tone }) => ($tone === "success" ? theme.color.successSurface : theme.color.errorSurface)};
`

const Message = styled.p`
  margin: 0;
`

const ItemsList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin: 0;
  padding: 0;
  list-style: none;
`

const ItemRow = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`

const ItemTitle = styled.span`
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`

/** Only a real ministry gets one: the swatch IS the label, and the catch-all has nothing to say. */
const Swatch = styled.span<{ $audience: Ministry }>`
  padding: ${({ theme }) => `${theme.spacing.xxs} ${theme.spacing.xs}`};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme, $audience }) => theme.audience[$audience].label};
  background-color: ${({ theme, $audience }) => theme.audience[$audience].block};
`

const fall = keyframes`
  from { transform: translateY(-0.5rem) rotate(0deg); opacity: 1; }
  to { transform: translateY(3.5rem) rotate(220deg); opacity: 0; }
`

const ConfettiField = styled.span`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`

const ConfettiPiece = styled.span<{ $offsetPercent: number; $tint: ConfettiTint; $delayMs: number }>`
  position: absolute;
  top: 0;
  left: ${({ $offsetPercent }) => $offsetPercent}%;
  width: 0.4rem;
  height: 0.4rem;
  border-radius: ${({ theme }) => theme.radius.sm};
  background-color: ${({ theme, $tint }) => ($tint === "accent" ? theme.color.accent : theme.brandSecondary[$tint])};
  animation: ${fall} 900ms ease-out ${({ $delayMs }) => $delayMs}ms both;
`

const ItemList = ({ items }: { readonly items: readonly EntitlementItem[] }): ReactElement | null => {
  if (items.length === 0) {
    return null
  }

  return (
    <ItemsList>
      {items.map(item => (
        <ItemRow key={item.id} data-qa-hook={`${QA}-item`}>
          <ItemTitle>{item.title}</ItemTitle>
          {item.audience === "adults" ? null : (
            <Swatch $audience={item.audience}>{ministryLabel(item.audience)}</Swatch>
          )}
        </ItemRow>
      ))}
    </ItemsList>
  )
}

/**
 * Rendered only while motion is allowed, not merely left un-animated:
 * `global.css` zeroes animation durations under reduced motion, which would
 * otherwise leave these ten squares sitting on the panel forever.
 */
const Confetti = (): ReactElement => (
  <ConfettiField aria-hidden="true">
    {CONFETTI_PIECES.map(piece => (
      <ConfettiPiece key={piece.id} $offsetPercent={piece.offsetPercent} $tint={piece.tint} $delayMs={piece.delayMs} />
    ))}
  </ConfettiField>
)

type SuccessOutcome = Extract<RedeemOutcome, { kind: "success" }>
type RejectedOutcome = Exclude<RedeemOutcome, { kind: "success" }>

interface SuccessPanelProps {
  readonly outcome: SuccessOutcome
  readonly prefersReducedMotion: boolean
  readonly onRedeemAnother: () => void
}

const SuccessPanel = ({ outcome, prefersReducedMotion, onRedeemAnother }: SuccessPanelProps): ReactElement => (
  <Panel $tone="success" data-qa-hook={`${QA}-success`}>
    {prefersReducedMotion ? null : <Confetti />}
    <Message>Your code was redeemed successfully.</Message>
    <ItemList items={outcome.items} />
    <RedeemAnother type="button" onClick={onRedeemAnother} data-qa-hook={`${QA}-another`}>
      Redeem another
    </RedeemAnother>
  </Panel>
)

const FailurePanel = ({ outcome }: { readonly outcome: RejectedOutcome }): ReactElement => {
  const message =
    outcome.kind === "error"
      ? "We couldn't reach Lifeway to redeem that code. Your code has not been used — please try again."
      : redeemFailureMessage(outcome.reason)
  /** ALREADY_REDEEMED is the only failure commerce hands items back for. */
  const items = outcome.kind === "failure" && outcome.reason === "ALREADY_REDEEMED" ? outcome.items : []

  return (
    <Panel $tone="error" data-qa-hook={`${QA}-failure`}>
      <Message>{message}</Message>
      <ItemList items={items} />
    </Panel>
  )
}

/**
 * MLC's Redeem Code panel (proposal 03), collapsed to a single button until a
 * customer asks for it — most visits to this page are not a redemption.
 *
 * POC: this is the one WRITE in the build, and it lands on whichever account
 * minted `MLC_ACCESS_TOKEN` rather than on the signed-in user. A redeemed code
 * is consumed and cannot be given back, so do not test with a code anyone is
 * waiting on. See POC.md's disclosure.
 */
export const RedeemCodeSection = (): ReactElement => {
  const [open, setOpen] = useState(false)
  const [code, setCode] = useState("")
  const submittedRef = useRef<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  /**
   * Guarded rather than called directly: `matchMedia` is absent in jsdom and in
   * any non-browser render, and confetti is decoration that must never be the
   * reason this panel throws. No support means no animation.
   */
  const [prefersReducedMotion] = useState(
    () => typeof window.matchMedia !== "function" || window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  )
  const { redeem, isPending, outcome, reset } = useRedeemCode()

  useEffect(() => {
    if (open && !outcome) {
      inputRef.current?.focus()
    }
  }, [open, outcome])

  /**
   * Firing on length alone would let one stray keystroke spend a redemption,
   * and a spent code cannot be handed back. The debounce gives a fast paste or
   * a mid-code correction time to land before anything is sent, and tracking
   * the code already submitted — rather than trusting `isPending` alone, which
   * flips back to false the instant a response arrives — keeps a later
   * re-render from redeeming that same code a second time.
   */
  useEffect(() => {
    const normalized = normalizeCode(code)
    const isComplete = normalized.replace(/[^A-Z0-9]/g, "").length >= AUTO_LENGTH

    if (!isComplete || isPending || submittedRef.current === normalized) {
      return
    }

    const timer = setTimeout(() => {
      submittedRef.current = normalized
      redeem(normalized)
    }, DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [code, isPending, redeem])

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setCode(normalizeCode(event.target.value))
    /** A stale verdict next to an edited code contradicts itself. */
    if (outcome) {
      reset()
    }
  }

  const redeemAnother = (): void => {
    reset()
    submittedRef.current = null
    setCode("")
    setOpen(true)
  }

  if (!open) {
    return (
      <Trigger>
        <Provenance source="mlc">
          <Button type="button" onClick={() => setOpen(true)} data-qa-hook={`${QA}-open`}>
            <Gift size={18} />
            Redeem a code
          </Button>
        </Provenance>
      </Trigger>
    )
  }

  if (outcome?.kind === "success") {
    return (
      <Provenance source="mlc">
        <SuccessPanel outcome={outcome} prefersReducedMotion={prefersReducedMotion} onRedeemAnother={redeemAnother} />
      </Provenance>
    )
  }

  return (
    <Provenance source="mlc">
      <Row>
        <Capsule>
          <CapsuleTrigger as="span" aria-hidden="true">
            <Gift size={18} />
            Redeem a code
          </CapsuleTrigger>
          <Input
            ref={inputRef}
            value={code}
            onChange={handleChange}
            aria-label="Digital access code"
            autoComplete="off"
            spellCheck={false}
            disabled={isPending}
            data-qa-hook={`${QA}-input`}
          />
        </Capsule>
        {isPending ? <Pending data-qa-hook={`${QA}-pending`}>Redeeming…</Pending> : null}
      </Row>
      {outcome ? <FailurePanel outcome={outcome} /> : null}
    </Provenance>
  )
}
